import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { WAutocomplete, WButton, WInputField } from '@wareclouds/wareclouds-ui';

import { COUNTRIES } from '@wareclouds/frontend-common';

import { CONTEXT } from '@/config/consts/commons';
import { useYupValidationTranslations } from '../../../../common/hooks';
import {
  completeProfileSchema,
  defaultValuesCompleteProfile,
} from '../../utils';
import {
  useGetRegions,
  useGetCities,
  useCompleteProfileSubmit,
  useProfilePhoneMask,
} from '../../hooks';
import {
  CreateAccountStepsIndicator,
  OnboardingPageLayout,
} from '../../../shared';

const IS_CL = CONTEXT === COUNTRIES.CL;

export default function CompleteProfilePage() {
  const { t } = useTranslation(['onboarding', 'button']);
  const translateError = useYupValidationTranslations();
  const translateFieldError = (error) => {
    const message = error?.message;
    if (typeof message === 'string' && message.includes(':')) {
      return t(message);
    }
    return translateError(error);
  };
  const { applyMask, placeholderKey } = useProfilePhoneMask();
  const { submit, isPending, isSuccess } = useCompleteProfileSubmit();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(completeProfileSchema),
    defaultValues: defaultValuesCompleteProfile,
    mode: 'onChange',
  });

  const selectedRegion = watch('region');
  const selectedRegionId = selectedRegion?.value ?? null;
  const skipRegionEffectOnce = useRef(true);

  const {
    data: regions = [],
    isPending: regionsLoading,
    isError: regionsError,
  } = useGetRegions();
  const {
    data: cities = [],
    isPending: citiesLoading,
    isFetching: citiesFetching,
  } = useGetCities(selectedRegionId);

  useEffect(() => {
    if (skipRegionEffectOnce.current) {
      skipRegionEffectOnce.current = false;
      return;
    }
    setValue('municipality', null, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: false,
    });
  }, [selectedRegion, setValue]);

  const onSubmit = async (data) => {
    await submit(data);
  };

  const hasRegionSelected =
    selectedRegionId !== '' &&
    selectedRegionId !== null &&
    selectedRegionId !== undefined;
  const municipalityDisabled =
    !hasRegionSelected || citiesLoading || citiesFetching;
  const citiesBusy = hasRegionSelected && (citiesLoading || citiesFetching);

  const steps = [
    { label: t('steps.profile') },
    { label: t('steps.training') },
    { label: t('steps.registration') },
  ];

  const inputSx = {
    '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
  };

  return (
    <OnboardingPageLayout
      footer={
        <WButton
          label={t('save', { ns: 'button' })}
          variant="contained"
          color="primary"
          fullWidth
          isLoading={isPending || isSuccess}
          isDisabled={!isValid || isPending || isSuccess}
          type="submit"
          form="complete-profile-form"
        />
      }
    >
      <CreateAccountStepsIndicator steps={steps} activeStep={0} />

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '2rem', lineHeight: 1.2 }}>👋🏼</Typography>
        <Typography variant="h5" fontWeight={500} sx={{ mt: 0.5 }}>
          {t('complete_profile.welcome')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {t('complete_profile.instruction')}
        </Typography>
      </Box>

      <form id="complete-profile-form" onSubmit={handleSubmit(onSubmit)}>
        {IS_CL ? (
          <>
            <WInputField
              name="given_name"
              control={control}
              translateError={translateFieldError}
              textFieldProps={{
                label: t('complete_profile.form.given_name.label'),
                placeholder: t('complete_profile.form.given_name.placeholder'),
                fullWidth: true,
                margin: 'normal',
                InputLabelProps: { shrink: true },
                sx: inputSx,
              }}
            />
            <WInputField
              name="middle_name"
              control={control}
              translateError={translateFieldError}
              helperText={t('complete_profile.form.middle_name.optional')}
              textFieldProps={{
                label: t('complete_profile.form.middle_name.label'),
                placeholder: t('complete_profile.form.middle_name.placeholder'),
                fullWidth: true,
                margin: 'normal',
                InputLabelProps: { shrink: true },
                sx: inputSx,
              }}
            />
            <WInputField
              name="first_last_name"
              control={control}
              translateError={translateFieldError}
              textFieldProps={{
                label: t('complete_profile.form.first_last_name.label'),
                placeholder: t(
                  'complete_profile.form.first_last_name.placeholder'
                ),
                fullWidth: true,
                margin: 'normal',
                InputLabelProps: { shrink: true },
                sx: inputSx,
              }}
            />
            <WInputField
              name="second_last_name"
              control={control}
              translateError={translateFieldError}
              textFieldProps={{
                label: t('complete_profile.form.second_last_name.label'),
                placeholder: t(
                  'complete_profile.form.second_last_name.placeholder'
                ),
                fullWidth: true,
                margin: 'normal',
                InputLabelProps: { shrink: true },
                sx: inputSx,
              }}
            />
          </>
        ) : (
          <>
            <WInputField
              name="first_name"
              control={control}
              translateError={translateFieldError}
              textFieldProps={{
                label: t('complete_profile.form.first_name.label'),
                placeholder: t('complete_profile.form.first_name.placeholder'),
                fullWidth: true,
                margin: 'normal',
                InputLabelProps: { shrink: true },
                sx: inputSx,
              }}
            />
            <WInputField
              name="last_name"
              control={control}
              translateError={translateFieldError}
              textFieldProps={{
                label: t('complete_profile.form.last_name.label'),
                placeholder: t('complete_profile.form.last_name.placeholder'),
                fullWidth: true,
                margin: 'normal',
                InputLabelProps: { shrink: true },
                sx: inputSx,
              }}
            />
          </>
        )}

        <WInputField
          name="phone"
          control={control}
          translateError={translateFieldError}
          textFieldProps={{
            onChange: (e) => {
              setValue('phone', applyMask(e.target.value), {
                shouldDirty: true,
                shouldValidate: true,
              });
            },
            label: t('complete_profile.form.phone.label'),
            placeholder: t(placeholderKey),
            fullWidth: true,
            margin: 'normal',
            inputMode: 'tel',
            autoComplete: 'tel',
            InputLabelProps: { shrink: true },
            sx: inputSx,
          }}
        />

        <WAutocomplete
          name="region"
          control={control}
          options={regions}
          loading={regionsLoading}
          helperText={
            regionsError ? t('complete_profile.form.region.load_error') : ''
          }
          translateError={translateFieldError}
          autocompleteProps={{
            disabled: regionsLoading || regionsError,
            noOptionsText: t('complete_profile.form.region.no_options'),
            isOptionEqualToValue: (a, b) =>
              String(a?.value) === String(b?.value),
          }}
          textFieldProps={{
            label: t('complete_profile.form.region.label'),
            placeholder: regionsLoading
              ? t('complete_profile.form.region.loading')
              : t('complete_profile.form.region.placeholder'),
            fullWidth: true,
            margin: 'normal',
            sx: inputSx,
            InputLabelProps: { shrink: true },
          }}
        />

        <WAutocomplete
          name="municipality"
          control={control}
          options={cities}
          loading={citiesBusy}
          translateError={translateFieldError}
          autocompleteProps={{
            disabled: municipalityDisabled,
            noOptionsText: t('complete_profile.form.municipality.no_options'),
            isOptionEqualToValue: (a, b) =>
              String(a?.value) === String(b?.value),
          }}
          textFieldProps={{
            label: t('complete_profile.form.municipality.label'),
            placeholder: !hasRegionSelected
              ? t('complete_profile.form.municipality.select_region_first')
              : citiesBusy
              ? t('complete_profile.form.municipality.loading')
              : t('complete_profile.form.municipality.placeholder'),
            fullWidth: true,
            margin: 'normal',
            sx: inputSx,
            InputLabelProps: { shrink: true },
          }}
        />
      </form>
    </OnboardingPageLayout>
  );
}
